// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { updateContext } from "@insite/client-framework/Context";
import {
    addToEndOfChain,
    ApiHandlerNoApiParameter,
    HasOnError,
    HasOnSuccess,
    makeHandlerChainAwaitable,
    markSkipOnCompleteIfOnErrorIsSet,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { createShipTo, updateShipTo as updateShipToApi } from "@insite/client-framework/Services/CustomersService";
import { updateSession } from "@insite/client-framework/Services/SessionService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getCurrentShipToState, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    AddOrUpdateShipTo,
    chain,
    DispatchCompleteUpdateShipTo,
    UpdateContext,
    UpdateSession,
} from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateShipTo";

import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { xGoogleAnalyticsState } from "@west/App/Store/Reducers";

type HandlerType = ApiHandlerNoApiParameter<
    {
        billToId: string;
        shipTo: ShipToModel;
        isEdit: boolean;
    } & HasOnSuccess<boolean> &
        HasOnError,
    ShipToModel
>;

export const XngAddOrUpdateShipTo: HandlerType = async props => {
    (props as any).isDelete = !(props as any).parameter.shipTo.properties.isActive || false;
    if (props.parameter.shipTo.isNew) {
        props.apiResult = await createShipTo({
            shipTo: props.parameter.shipTo,
        });
    } else if (props.parameter.isEdit) {
        try {
            props.apiResult = await updateShipToApi({
                billToId: props.parameter.billToId,
                shipTo: props.parameter.shipTo,
            });
            if ((props as any).isDelete) {
                props.apiResult = {} as ShipToModel;
            }
        } catch (error) {
            props.dispatch({
                type: "Pages/OnePageCheckout/CompleteUpdateShipTo",
                shipTo: {} as ShipToModel,
            });
            if ((props as any).onError) {
                markSkipOnCompleteIfOnErrorIsSet(props);
                props.parameter.onError?.();
            }
            return false;
        }
    } else {
        props.apiResult = props.parameter.shipTo;
    }
    // XNG-Changes : added a condition to stop the deleted address from being fetched
    if (!(props as any).isDelete) {
        // reload to make sure we get validation
        await makeHandlerChainAwaitable(loadShipTo)({
            billToId: props.parameter.billToId,
            shipToId: props.apiResult.id,
        })(props.dispatch, props.getState);
    }
};

// XNG-Changes: dont change default shipTo in onePageCheckout on new address creation or selection of new address.

export const XngUpdateContext: HandlerType = props => {
    if (props.apiResult) {
        updateContext({
            billToId: props.parameter.billToId,
            settings: getSettingsCollection(props.getState()),
        });
    }
};

export const XngUpdateSession: HandlerType = async props => {
    if (props.apiResult) {
        await updateSession({
            session: {
                billToId: props.parameter.billToId,
                customerWasUpdated: true,
            },
        });
    }
};

// XNG-Changes: when an address is deleted, dont update the shipTo address for the onePageCheckout and remove spinner
export const XngDispatchCompleteUpdateShipTo: HandlerType = props => {
    const state = props.getState();
    const { cartId } = state.pages.onePageCheckout;
    const cart = cartId ? getCartState(state, cartId).value! : getCurrentCartState(state).value!;

    const shipToState = (props as any).isDelete
        ? getShipToState(state, cart?.shipToId)
        : getShipToState(state, props.apiResult.id);

    props.dispatch({
        type: "Pages/OnePageCheckout/CompleteUpdateShipTo",
        shipTo: shipToState.value!,
    });
};

export const XngGaTrackSaveShipping: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    const { cartId } = state.pages.onePageCheckout;
    const cart = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    if (state.xGoogleAnalytics.checkoutFlowEvents && cart.value && !cart.isLoading) {
        (window as any)._GaCheckoutFunnel(
            2,
            cart.value,
            state.xGoogleAnalytics.includeProductBrand,
            state.xGoogleAnalytics.includeProductCategory,
            state.xGoogleAnalytics.affiliation,
            state.xGoogleAnalytics.authenticationState,
        );
    }
};

addToEndOfChain(chain, XngGaTrackSaveShipping);
replaceInChain(chain, UpdateContext, XngUpdateContext);
replaceInChain(chain, UpdateSession, XngUpdateSession);
replaceInChain(chain, AddOrUpdateShipTo, XngAddOrUpdateShipTo);
replaceInChain(chain, DispatchCompleteUpdateShipTo, XngDispatchCompleteUpdateShipTo);
